/**
 * CrmPreview — standalone full-screen client dashboard preview.
 *
 * Fixes the "Open in New Tab" 404: CustomerPreviewDialog opens /crm/preview?...
 * with business_id/company_name/industry/demo_user_name. This route reads those
 * params (falling back to the demo_preview_context localStorage set by the dialog)
 * and renders the same UnifiedDashboard in preview mode. Same-origin tab keeps the
 * staff auth token in localStorage, so the authenticated context carries over.
 */
import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClientProvider } from '@/contexts/ClientContext';
import UnifiedDashboard from '@/components/dashboards/UnifiedDashboard';

export default function CrmPreview() {
  const [params] = useSearchParams();

  const ctx = useMemo(() => {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem('demo_preview_context') || '{}'); } catch { /* ignore */ }
    const industry = params.get('industry') || stored.industry || 'hospitality';
    return {
      business_id: params.get('business_id') || stored.business_id,
      company_name: params.get('company_name') || stored.company_name,
      industry,
      demo_user_name: params.get('demo_user_name') || stored.demo_user_name || 'Demo User',
      modules: stored.modules,
    };
  }, [params]);

  useEffect(() => {
    document.title = `${ctx.company_name || 'Client'} — Corvana Preview`;
    try {
      localStorage.setItem('demo_preview_context', JSON.stringify({ ...ctx, is_demo_preview: true }));
    } catch { /* ignore */ }
  }, [ctx]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="crm-preview-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <ClientProvider>
          <UnifiedDashboard previewIndustry={ctx.industry} previewContext={ctx} />
        </ClientProvider>
      </div>
    </div>
  );
}
